const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function for API calls
async function fetchAPI(endpoint, options = {}) {
    const token = localStorage.getItem('token');

    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Bir hata oluştu');
    }

    return data;
}

// Auth API
export const authAPI = {
    register: (userData) => fetchAPI('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
    }),

    login: (credentials) => fetchAPI('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
    }),

    getMe: () => fetchAPI('/auth/me'),

    logout: () => fetchAPI('/auth/logout', { method: 'POST' }),

    forgotPassword: (email) => fetchAPI('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
    }),

    resetPassword: (token, password) => fetchAPI(`/auth/reset-password/${token}`, {
        method: 'POST',
        body: JSON.stringify({ password }),
    }),

    verifyEmail: (token) => fetchAPI(`/auth/verify-email/${token}`),
};

// User API
export const userAPI = {
    updateProfile: (data) => fetchAPI('/user/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    updateSettings: (settings) => fetchAPI('/user/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
    }),

    changePassword: (passwords) => fetchAPI('/user/password', {
        method: 'PUT',
        body: JSON.stringify(passwords),
    }),

    getDashboard: () => fetchAPI('/user/dashboard'),

    deleteAccount: (password) => fetchAPI('/user/account', {
        method: 'DELETE',
        body: JSON.stringify({ password }),
    }),

    exportData: () => fetchAPI('/user/export'),

    // Support Tickets
    getSupportTickets: () => fetchAPI('/user/support'),

    getSupportTicket: (id) => fetchAPI(`/user/support/${id}`),

    createSupportTicket: (data) => fetchAPI('/user/support', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    replyToSupportTicket: (id, message) => fetchAPI(`/user/support/${id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message }),
    }),
};

// Subscription API
export const subscriptionAPI = {
    getStatus: () => fetchAPI('/subscription/status'),

    getPlans: () => fetchAPI('/subscription/plans'),

    createCheckout: (plan) => fetchAPI('/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan }),
    }),

    createPortal: () => fetchAPI('/subscription/portal', { method: 'POST' }),

    cancel: () => fetchAPI('/subscription/cancel', { method: 'POST' }),

    resume: () => fetchAPI('/subscription/resume', { method: 'POST' }),
};

// Calculation API
export const calculationAPI = {
    getAll: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return fetchAPI(`/calculation?${query}`);
    },

    getOne: (id) => fetchAPI(`/calculation/${id}`),

    create: (data) => fetchAPI('/calculation', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    update: (id, data) => fetchAPI(`/calculation/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    delete: (id) => fetchAPI(`/calculation/${id}`, { method: 'DELETE' }),

    deleteAll: (keepFavorites = false) => fetchAPI(`/calculation?keepFavorites=${keepFavorites}`, {
        method: 'DELETE',
    }),

    getFavorites: () => fetchAPI('/calculation/favorites'),
};

// Reminder API
export const reminderAPI = {
    getAll: () => fetchAPI('/reminder'),

    getOne: (id) => fetchAPI(`/reminder/${id}`),

    create: (data) => fetchAPI('/reminder', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    update: (id, data) => fetchAPI(`/reminder/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    delete: (id) => fetchAPI(`/reminder/${id}`, { method: 'DELETE' }),

    markNotified: (id) => fetchAPI(`/reminder/${id}/notified`, { method: 'POST' }),
};

// Admin API
export const adminAPI = {
    // Dashboard
    getDashboard: () => fetchAPI('/admin/dashboard'),

    // Users
    getUsers: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return fetchAPI(`/admin/users?${query}`);
    },

    getUser: (id) => fetchAPI(`/admin/users/${id}`),

    updateUser: (id, data) => fetchAPI(`/admin/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    deleteUser: (id) => fetchAPI(`/admin/users/${id}`, { method: 'DELETE' }),

    // User Actions
    banUser: (id, data) => fetchAPI(`/admin/users/${id}/ban`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    unbanUser: (id) => fetchAPI(`/admin/users/${id}/unban`, { method: 'POST' }),

    grantPremium: (id, data) => fetchAPI(`/admin/users/${id}/grant-premium`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    revokePremium: (id) => fetchAPI(`/admin/users/${id}/revoke-premium`, { method: 'POST' }),

    makeAdmin: (id, permissions) => fetchAPI(`/admin/users/${id}/make-admin`, {
        method: 'POST',
        body: JSON.stringify({ permissions }),
    }),

    removeAdmin: (id) => fetchAPI(`/admin/users/${id}/remove-admin`, { method: 'POST' }),

    // Settings & Logs
    getSettings: () => fetchAPI('/admin/settings'),
    getActivityLogs: () => fetchAPI('/admin/logs'),
};

const api = {
    auth: authAPI,
    user: userAPI,
    subscription: subscriptionAPI,
    calculation: calculationAPI,
    reminder: reminderAPI,
    admin: adminAPI,
};

export default api;
